import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'ghost' | 'mini' | 'grave';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: React.ReactNode;
}

const variants: Record<ButtonVariant, string> = {
  /* gold fill, near-black text, per approved system */
  primary: 'h-7 px-3 rounded bg-acc text-acc-ink text-small font-medium hover:bg-acc-hov',
  ghost: 'h-7 px-3 rounded border border-line-2 text-small font-medium text-ink-2 hover:bg-hov hover:text-ink',
  /* Actions that cannot be taken back. Outlined in the bad token rather than
     filled, so it reads as consequential without competing with the primary
     action for attention. Naming a package closes structuring for good;
     delivery copies a corpus into a third party's bucket; dropping files
     removes them from what ships. All three looked exactly like Sync now. */
  grave: 'h-7 px-3 rounded border border-bad bg-bad-soft text-small font-medium text-bad hover:bg-bad/15',
  /* toolbar-scale control; extended hit area keeps it comfortable */
  mini: 'relative h-6 px-2 rounded border border-line-2 text-caption text-ink-2 hover:bg-hov hover:text-ink after:absolute after:-inset-1 after:content-[""]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap transition-colors duration-fast ease-standard active:scale-[0.985] disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        icon && <span className="shrink-0" aria-hidden="true">{icon}</span>
      )}
      {children}
    </button>
  );
});
